import React, { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useWebSocket, ReadyState } from './useWebSocket';
import type { SendMessage } from './useWebSocket';
import { UserContext } from "../components/auth/auth";
import { getCsrfToken } from "../components/utils/csrf";
import { ServerErrorMessage, ServerObjectMessage, ServerUpdate, ServerLimitMessage, OBJECT_TYPE } from "../eventsDispatcher/eventTypes";
import useWheelId from "../hooks/useWheelId";
import { useLicense } from "../hooks/useLicense";
import useActualWheelId from "@/hooks/useActualWheelId";
import SlowSyncModal from "../components/growth/SlowSyncModal";


export interface WebsocketContextData {
    connected: boolean
    syncDone: boolean
    setSyncDone: React.Dispatch<React.SetStateAction<boolean>>
    setSyncInProgress: React.Dispatch<React.SetStateAction<boolean>>
    syncInProgress: boolean
    pendingObjectCountRef: React.MutableRefObject<number>
    sendMessage: SendMessage
    lastJsonMessage: ServerUpdate | ServerObjectMessage | ServerErrorMessage | ServerLimitMessage
    localChannel: BroadcastChannel | undefined
    reconnect: () => void
    limitWarnings: Set<OBJECT_TYPE>
    addLimitWarning: (object_type: OBJECT_TYPE) => void
    clearLimitWarnings: () => void
}

export const WebSocketContext = React.createContext<WebsocketContextData | undefined>(undefined);


export const WebsocketProvider: React.FC<React.PropsWithChildren> = ({ children }) => {

    const user = useContext(UserContext);
    const license = useLicense();
    const [syncDone, setSyncDone] = useState(false);
    const [syncInProgress, setSyncInProgress] = useState(false);
    const pendingObjectCountRef = useRef(0);
    const [channel, setChannel] = useState<BroadcastChannel>();
    const [reconnectKey, setReconnectKey] = useState(0);
    const wheelId = useActualWheelId();
    const [limitWarnings, setLimitWarnings] = useState<Set<OBJECT_TYPE>>(new Set());

    const addLimitWarning = useCallback((object_type: OBJECT_TYPE) => {
        setLimitWarnings(prev => new Set(prev).add(object_type));
    }, []);

    const clearLimitWarnings = useCallback(() => {
        setLimitWarnings(new Set());
    }, []);

    const reconnect = useCallback(() => {
        setReconnectKey(prev => prev + 1);
    }, []);

    const getUrl = useCallback(async () => {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const port = window.location.port;
        const domain = window.location.hostname;
        if (user.authorized) {
            let data = await fetch(`/api/updates/ws_token?wheel_uuid=${wheelId}`, { method: "POST", headers: { 'X-Csrftoken': getCsrfToken() } });
            let json = await data.json();
            let token = json?.token || "";
            return `${protocol}//${domain}:${port}/ws/${token}`
        }
        return "";
    }, [user.authorized, user.email, wheelId, reconnectKey]);

    const { lastJsonMessage, readyState, sendMessage } = useWebSocket<ServerUpdate>(getUrl, {
        shouldReconnect: () => true,
        reconnectAttempts: 1000000,
        reconnectInterval: 10000,
        share: true,
    });

    useEffect(() => {
        if (readyState === ReadyState.OPEN) {
            setSyncDone(false);
            setSyncInProgress(false);
            pendingObjectCountRef.current = 0;
            // Сбрасываем предупреждения при подключении (переподключении)
            clearLimitWarnings();
        }
    }, [readyState, clearLimitWarnings]);

    useEffect(() => {
        setChannel(new BroadcastChannel('artifacts-channel'));
    }, [])

    // Реконнект при активации окна, если офлайн
    useEffect(() => {
        const handleFocus = () => {
            if (readyState !== ReadyState.OPEN) {
                reconnect();
            }
        };

        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [readyState, reconnect]);

    return <WebSocketContext.Provider value={{ 
        connected: readyState === ReadyState.OPEN, 
        syncDone, 
        sendMessage, 
        lastJsonMessage, 
        setSyncDone, 
        setSyncInProgress, 
        syncInProgress, 
        pendingObjectCountRef,
        localChannel: channel, 
        reconnect, 
        limitWarnings, 
        addLimitWarning, 
        clearLimitWarnings 
    }}>
        {children}
        <SlowSyncModal />
    </WebSocketContext.Provider>
}